import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useFormContext, Controller } from "react-hook-form";
import { FormField } from "../../FormEngine/types";
import FormFieldComponent from "../../FormEngine/FormFieldComponent";
import { CategoryType } from "../../../types/PatientFormTypes";
import { toast } from "react-toastify";
import _ from "lodash";
import moment from "moment";

type CardField = Exclude<FormField, { type: "group-input" }>;

interface CustomCardListProps {
  name: string;
  label: string;
  addButtonText: string;
  helperText?: string;
  cardFields: CardField[];
  category: CategoryType;
  onSaveCategoryData: (data: any, category: CategoryType) => Promise<boolean>;
  onSaveItem?: (data: any, type: 'add' | 'edit' | 'delete') => Promise<void>;
  newItemTemplate: Record<string, any>;
  hideFieldData?: boolean;
}

interface CardViewProps {
  item: Record<string, any>;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  cardFields: CardField[];
  hideFieldData?: boolean;
}

const CardView: React.FC<CardViewProps> = ({
  item,
  index,
  onEdit,
  onDelete,
  cardFields,
  hideFieldData,
}) => {
  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ pr: 1 }} noWrap>
            {item[cardFields[0]?.name] || `Item ${index + 1}`}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <IconButton onClick={onEdit} size="small" color="primary">
              <EditIcon />
            </IconButton>
            <IconButton onClick={onDelete} size="small" color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Stack>
        {!hideFieldData && (
          <>
            <Divider sx={{ mb: 2, mt: 1 }} />
            <Stack spacing={1}>
              {cardFields.map((field) => {
                let value = item[field.name]?.toString();
                if (!value) return null;

                switch (field.type) {
                  case "date":
                    value = moment(value).format(field.fieldProps?.formatValue || "MM/DD/YYYY");
                    break;
                  case "checkbox":
                    value = value ? "Yes" : "No";
                    break;
                  default:
                    break;
                }

                return (<Typography key={field.name} variant="body2">
                  <strong>{field.label || field.title}:</strong>{" "}
                  {value}
                </Typography>)
              })}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

interface ItemFormProps {
  prefix: string;
  onSave: (data: Record<string, any>) => void;
  onCancel: () => void;
  cardFields: CardField[];
}

const ItemForm: React.FC<ItemFormProps> = ({
  prefix,
  onSave,
  onCancel,
  cardFields,
}) => {
	const { control, getValues, setError } = useFormContext();

  const validateForm = (): boolean => {
		const formData = getValues(prefix);
    
    let ret = true;
    cardFields.forEach((field) => {
      if (field.rule && !formData[field.name]) {
        ret = false;
        setError(`${prefix}.${field.name}`, { message: `${field.label} is required` });
      }
    });
    
    return ret;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

		const formData = getValues(prefix);
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
      <CardContent>
				<Stack spacing={1}>
					{cardFields.map((field, i) => (
						<React.Fragment key={field.name + i}>
							<FormFieldComponent
								field={{ ...field, name: `${prefix}.${field.name}` }}
								control={control}
							/>
						</React.Fragment>
					))}
				</Stack>
				
				<Stack direction="row" spacing={1} sx={{ mt: 3 }}>
					<Button
						variant="contained"
						startIcon={<SaveIcon sx={{ color: "primary.contrastText" }} />}
						onClick={handleSubmit}
					>
						Save
					</Button>
					<Button
						variant="outlined"
						startIcon={<CancelIcon sx={{ color: "action.active" }} />}
						onClick={onCancel}
            sx={{ backgroundColor: 'background.paper' }}
					>
						Cancel
					</Button>
				</Stack>
      </CardContent>
    </Card>
  );
};

export const CustomCardList: React.FC<CustomCardListProps> = ({
  name,
  label,
  addButtonText,
  helperText,
  cardFields,
  category,
  onSaveItem,
  onSaveCategoryData,
  newItemTemplate,
  hideFieldData,
}) => {
  const { control, getValues, setValue, watch } = useFormContext();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [preEditedValues, setPreEditedValues] = useState<Record<string, any>>({});

  const items = watch(name, getValues(name) || []);

  const handleAddItem = () => {
    if (isAdding) {
      toast.warning("Please save the item you are adding first.");
      return;
    }
    const currentItems = getValues(name) || [];
    setValue(name, [...currentItems, _.cloneDeep(newItemTemplate)], { shouldValidate: false, shouldDirty: true });
    setIsAdding(true);
    setEditingIndex(currentItems.length);
  };

  const handleEditItem = (index: number) => {
    if (isAdding) {
      toast.warning("Please save the item you are adding before editing.");
      return;
    }
    const currentItems = getValues(name) || [];
    setEditingIndex(index);
    setPreEditedValues(_.cloneDeep(currentItems[index]));
  };

  const handleDeleteItem = async (index: number) => {
    const currentItems = getValues(name) || [];
    const deletedItem = currentItems.splice(index, 1)[0];

    setValue(name, currentItems, { shouldValidate: true, shouldDirty: true });

    const existingValues = getValues();
    const updatedValues = { ...existingValues, [name]: currentItems };
    await onSaveCategoryData(updatedValues, category);

    if (onSaveItem) {
      await onSaveItem(deletedItem, 'delete');
    }

    if (editingIndex === index) {
      setEditingIndex(null);
      setIsAdding(false);
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleSaveItem = async (data: Record<string, any>, index: number) => {
    const currentItems = getValues(name) || [];

    const existingValues = getValues();
    const updatedValues = { ...existingValues, [name]: currentItems };
    await onSaveCategoryData(updatedValues, category);

    if (onSaveItem) {
      await onSaveItem(currentItems[index], isAdding ? 'add' : 'edit');
    }

    setEditingIndex(null);
    setIsAdding(false);
  };

  const handleCancelEdit = () => {
    const currentItems = getValues(name) || [];
    if (isAdding) {
      currentItems.splice(currentItems.length - 1, 1);
      setValue(name, currentItems, { shouldValidate: false, shouldDirty: true });
    } else if (editingIndex !== null) {
      currentItems[editingIndex] = preEditedValues;
      setValue(name, currentItems, { shouldValidate: false, shouldDirty: true });
    }
    setEditingIndex(null);
    setIsAdding(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field, fieldState }) => (
        <Box>
          <Stack spacing={2}>
            {items.map((item: Record<string, any>, index: number) =>
              editingIndex === index ? (
                <Box key={`${name}-item-edit-${index}`}>
                  <ItemForm
                    key={`${name}-form-${item?.id || index}`}
                    prefix={`${name}.${index}`}
                    onSave={(data) => handleSaveItem(data, index)}
                    onCancel={handleCancelEdit}
                    cardFields={cardFields}
                  />
                </Box>
              ) : (
                <CardView
                  key={`${name}-view-${item?.id || index}`}
                  item={item}
                  index={index}
                  onEdit={() => handleEditItem(index)}
                  onDelete={() => handleDeleteItem(index)}
                  cardFields={cardFields}
                  hideFieldData={hideFieldData}
                />
              )
            )}

            {!isAdding && (
              <Button
                startIcon={<AddIcon sx={{ color: "action.active" }} />}
                onClick={handleAddItem}
                variant="outlined"
                sx={{ width: "fit-content", mt: 1 }}
              >
                Add {items.length > 0 ? "Another" : ""} {addButtonText}
              </Button>
            )}
          </Stack>

          {fieldState.error && (
            <FormHelperText error sx={{ mt: 1 }}>
              {fieldState.error.message || (fieldState.error as any)?.root?.message}
            </FormHelperText>
          )}
          {helperText && !fieldState.error && (
            <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>
          )}
        </Box>
      )}
    />
  );
};

export default CustomCardList;
